<template>
  <section class="location-row" :class="{ desktop: $isDesktop }">
    <div class="map-holder">
      <client-only>
        <campsite-map-modal ref="mapModal" :campsite="campsite">
          <template #modal-trigger>
            <lazy-campsites-map v-bind="mapProps" :centre="point">
              <template #popup="{ markerData }">
                {{ markerData.name }}
              </template>
            </lazy-campsites-map>
          </template>
        </campsite-map-modal>
      </client-only>
    </div>
    <div>
      <p class="cs-name">{{ name }}</p>
      <p class="full-address">
        {{ fullAddress }}
        <copy-button :text-to-copy="fullAddress" />
      </p>
      <translate
        v-if="accessCode"
        :tt="{ accessCode }"
        tag="p"
        class="accesscode"
      >
        Access/gate code: {accessCode}
      </translate>
      <p v-if="tel">
        <a :href="`tel:${tel}`">{{ tel }}</a>
        <template v-if="secondaryPhone">
          <translate>or</translate><br />
          <a :href="`tel:${secondaryPhone}`">{{ secondaryPhone }}</a>
        </template>
      </p>
      <p v-if="email">
        <a :href="`mailto:${email}`" class="allow-wrap">{{ email }}</a>
      </p>
      <campsite-info-directions
        v-if="$isDesktop"
        :name="campsite.name"
        :full-address="campsite.fullAddress + ', ' + campsite.postcode"
        :directions="directions"
        :point="campsite.point"
        :user-address="userAddress"
      />
    </div>
  </section>

  <campsite-info-directions
    v-if="!$isDesktop"
    :name="campsite.name"
    :full-address="campsite.fullAddress + ', ' + campsite.postcode"
    :directions="directions"
    :point="campsite.point"
    :user-address="userAddress"
  />
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import type { MapCampsite } from '~/apps/maps/domain/MapsCampsite'
import { CampsitesMapProps } from '~/apps/maps/components/campsites/CampsitesMapProps'
import type { UserBooking } from '~/apps/user-bookings/schemas'

@Component
class CampsiteInformation extends Vue {
  @Prop({ required: true })
  campsite: UserBooking['campsite']

  @Prop({ required: true })
  name: string

  @Prop({ required: true })
  slug: string

  @Prop({ required: true })
  email: string

  @Prop({ required: true })
  fullAddress: string

  @Prop({ required: true })
  directions: string

  @Prop({ required: false })
  accessCode: string

  @Prop({ required: true })
  path: string

  @Prop({ required: false })
  point: string | null

  @Prop({ required: true })
  tel: string

  @Prop({ required: true })
  secondaryPhone: string

  @Prop({ required: true })
  userAddress: string

  @Prop({ required: true })
  mapCampsite: MapCampsite

  get mapProps(): CampsitesMapProps {
    return {
      campsites: [this.mapCampsite],
      isCampsiteDetails: true,
      zoomControl: false,
      initialZoom: 7,
    }
  }
}

export default toNative(CampsiteInformation)
</script>
<style scoped>
.allow-wrap {
  word-break: break-all;
}

.location-row {
  display: flex;
  flex-direction: row;
  justify-content: start;
  gap: 1em;
  margin: 1em 0 0;
}

.location-row p {
  margin: 0 0 0.5em 0;
}

.location-row .cs-name {
  font-weight: bold;
  margin: 0;
}

.full-address {
  text-wrap-style: balance;
}

.map-holder {
  display: block;
  position: relative;
  flex-shrink: 0;
  background-color: #e6e4e0;
  border-radius: var(--border-radius);
  overflow: hidden;
  position: relative;
  cursor: pointer;
}

.map-holder,
.map-holder :deep(.pu-map) {
  width: 6em;
  min-height: 6em;
  isolation: isolate;
}

.map-holder :deep(.pu-map .leaflet-control-attribution) {
  display: none;
}

.map-holder :deep(.cs-map-modal),
.map-holder :deep(.modal-trigger-wrapper) {
  height: 100%;
}

.map-holder :deep(.modal-trigger-wrapper:before) {
  content: '';
  background-color: white;
  position: absolute;
  width: 3em;
  height: 3em;
  border-radius: 6em;
  display: block;
  bottom: -1.25em;
  right: -1.5em;
  z-index: 1;
}

.map-holder :deep(.modal-trigger-wrapper:after) {
  background: url('~/assets/images/magnify.svg') no-repeat center center;
  content: '';
  height: 1.25em;
  width: 1em;
  bottom: 0;
  right: 0;
  position: absolute;
  display: block;
  z-index: 2;
}

.accesscode {
  background-color: #e1e5ea80;
  border-radius: var(--border-radius);
  padding: 0.25em;
  display: inline-flex;
  align-items: center;
}

.location-row.desktop .map-holder {
  max-width: 18em;
  width: 50%;
  height: auto;
  min-height: 15em;
}

.location-row.desktop .map-holder :deep(.pu-map) {
  width: 100%;
  height: 100%;
}

.location-row.desktop {
  gap: 2em;
}

.location-row.desktop p + p {
  margin: 1em 0 0 0;
}

.location-row.desktop .full-address {
  margin: 0.25em 0 1em 0;
}

.location-row.desktop .accesscode {
  margin-top: 0;
}

.location-row.desktop .map-holder :deep(.modal-trigger-wrapper:before) {
  width: 5em;
  height: 5em;
  bottom: -2.5em;
  right: -2.5em;
}

.location-row.desktop .map-holder :deep(.modal-trigger-wrapper:after) {
  height: 1.5em;
  width: 1.5em;
}

.location-row.desktop :deep(.location-actions a) {
  flex: 0 1 auto;
}
</style>
